import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { destinations } from '../data/destinations';
import type { Destination } from '../data/destinations';
import { destinationGuides, type DestinationGuide } from '../data/destination-guides';
import { getPageDates, type PageDates } from '../data/content-dates';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Byline from '../components/Byline';
import NotFound from './NotFound';
import { MapPin, Info, Compass } from 'lucide-react';

const pagePath = (destination: Destination): string => `/destinations/${destination.slug}`;

const pageUrl = (destination: Destination): string => `https://www.eclipsegroup.in${pagePath(destination)}`;

const pageTitle = (destination: Destination): string => `${destination.name} — Voyara AI travel intelligence`;

// A plain function rather than a hook: the component returns early for
// unknown slugs (rules of hooks). The itinerary is not a single place, so it
// is described as a Trip with no map pin.
function buildSchema(destination: Destination, guide: DestinationGuide, dates: PageDates | undefined) {
  const url = pageUrl(destination);
  const subject = destination.category === 'itinerary'
    ? {
        "@type": "Trip",
        "@id": `${url}#trip`,
        "name": destination.name,
        "description": guide.intro
      }
    : {
        "@type": "Place",
        "@id": `${url}#place`,
        "name": destination.name,
        "description": guide.intro,
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": destination.coordinates.lat,
          "longitude": destination.coordinates.lon
        },
        "hasMap": `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination.name)}`
      };

  // The guide itself: who wrote it and when, as the byline shows.
  const page = {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    "url": url,
    "name": pageTitle(destination),
    "about": { "@id": subject["@id"] },
    "author": {
      "@type": "Organization",
      "@id": "https://www.eclipsegroup.in/#organization",
      "name": "Eclipse Group"
    },
    ...(dates && { "datePublished": dates.published, "dateModified": dates.modified })
  };

  // Mirrors the FAQ list rendered on the page.
  const faq = {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    "mainEntity": guide.faqs.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  return {
    "@context": "https://schema.org",
    "@graph": [page, subject, faq]
  };
}

const DestinationPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const destination = destinations.find((d) => d.slug === slug);

  if (!destination) {
    return <NotFound />;
  }

  const guide = destinationGuides[destination.slug];
  const relatedDestinations = destination.relatedSlugs
    .map((s) => destinations.find((d) => d.slug === s))
    .filter((d): d is Destination => d !== undefined);
  const dates = getPageDates(pagePath(destination));
  const schema = buildSchema(destination, guide, dates);

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-blue-500/30">
      <SEO
        title={pageTitle(destination)}
        description={guide.intro}
        canonical={pageUrl(destination)}
        schema={schema}
      />
      <main className="pt-32 pb-20 global-container max-w-4xl mx-auto px-6">
        <div>
          <Breadcrumbs
            trail={[
              { name: 'Explore', path: '/explore' },
              { name: destination.name, path: pagePath(destination) },
            ]}
          />

          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-blue-400 text-sm font-bold tracking-[0.2em] uppercase">
              {destination.category} / {destination.country}
            </span>
          </div>

          <h1 className="font-serif text-[clamp(2.5rem,6vw,4.5rem)] leading-none mb-10 tracking-tight">
            Explore <span className="italic text-white/70">{destination.name}.</span>
          </h1>
          {dates && <Byline dates={dates} className="-mt-5 mb-10" />}

          <div className="liquid-glass-strong rounded-3xl p-8 md:p-12 mb-16 border border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none">
              <Compass className="w-48 h-48" />
            </div>
            
            <p className="text-secondary text-lg leading-relaxed mb-12 font-medium max-w-2xl text-pretty">
              {guide.intro}
            </p>

            <div className="flex items-center gap-3 mb-6">
              <Info className="w-5 h-5 text-white/40" />
              <h2 className="text-sm font-bold tracking-[0.1em] uppercase text-white/60">Highlights</h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-4">
              {guide.highlights.map((highlight) => (
                <li key={highlight} className="flex items-center gap-3 text-white/90 font-medium">
                  <div className="w-1 h-1 rounded-full bg-blue-500/40 shrink-0" />
                  {highlight}
                </li>
              ))}
            </ul>
          </div>

          {guide.sections.map((section) => (
            <section key={section.heading} className="mb-14 max-w-3xl">
              <h2 className="font-serif text-2xl md:text-3xl mb-5">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="text-secondary leading-relaxed mb-5">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}

          <section className="mb-20 pt-12 border-t border-white/10">
            <h2 className="font-serif text-2xl md:text-3xl mb-8">Frequently asked questions</h2>
            <div className="space-y-6">
              {guide.faqs.map((item) => (
                <div key={item.question} className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                  <h3 className="text-lg font-medium text-white mb-3">{item.question}</h3>
                  <p className="text-secondary leading-relaxed text-sm">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Automated Internal Linking (Topical Authority Clusters) */}
          {relatedDestinations.length > 0 && (
            <div className="mb-20">
              <h2 className="text-xs font-bold tracking-[0.3em] uppercase text-white/55 mb-8">Related destinations</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedDestinations.map((rel) => (
                  <Link 
                    key={rel.slug}
                    to={`/destinations/${rel.slug}`}
                    className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.05] hover:border-white/10 transition-all group"
                  >
                    <span className="text-xs text-blue-400/60 uppercase tracking-widest mb-2 block">{rel.category}</span>
                    <h3 className="text-primary font-medium group-hover:text-white transition-colors">{rel.name}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="text-center">
            <Link 
              to="/corporate-travel" 
              className="inline-block px-10 py-4 rounded-xl bg-white/5 border border-white/10 text-primary text-sm font-bold tracking-[0.15em] uppercase hover:bg-white/10 transition-all duration-300"
            >
              Coordinate Travel to {destination.name}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DestinationPage;
